/* eslint-disable react/prop-types */
import { Formik, Form } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Import component UI sổ mà bạn đã có (Đảm bảo file này nằm đúng đường dẫn)
import DetailLedgerVatExemptionReduction from "./info";

// Tạm gọi service API cho sổ này là VatExemptionReductionService
import { VatExemptionReductionService } from "services/vat-exemption-reduction";
import { getPrintTemplate } from "./components/printTemplate";

const today = new Date();

// 1. Khởi tạo giá trị mặc định khớp chuẩn với Sổ S63-DN
const initialValues = {
  company: "",
  companyAddress: "",
  year: today.getFullYear().toString(),
  openingExemptedAmount: "",
  openingAlreadyExemptedAmount: "",
  items: [],
  totalArisingExemptedAmount: "",
  totalArisingAlreadyExemptedAmount: "",
  closingExemptedAmount: "",
  closingAlreadyExemptedAmount: "",
  totalPages: "",
  openingDate: "",
  signingDay: today.getDate().toString(),
  signingMonth: (today.getMonth() + 1).toString(),
  signingYear: today.getFullYear().toString(),
  preparerName: "",
  chiefAccountantName: "",
  legalRepresentativeName: "",
};

function DetailLedgerVatExemptionReductionDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();
  const [getData, setGetData] = useState({});
  const [data, setData] = useState({});

  const [formInitials, setFormInitials] = useState(initialValues);

  // 2. Fetch dữ liệu từ API và map vào Form
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await VatExemptionReductionService.getById(id);
        const apiData = response.data ? response.data : response;

        // Map mảng chi tiết phát sinh
        const mappedItems = (apiData.items || []).map((item) => ({
          documentNumber: item.documentNumber || "",
          documentDate: item.documentDate
            ? new Date(item.documentDate).toLocaleDateString("vi-VN")
            : "",
          description: item.description || "",
          exemptedAmount: item.exemptedAmount || "",
          alreadyExemptedAmount: item.alreadyExemptedAmount || "",
        }));

        // Parse ngày ký để map vào form UI (nếu có)
        let sDay = initialValues.signingDay;
        let sMonth = initialValues.signingMonth;
        let sYear = initialValues.signingYear;
        if (apiData.signingDate) {
          const sDate = new Date(apiData.signingDate);
          sDay = sDate.getDate().toString();
          sMonth = (sDate.getMonth() + 1).toString();
          sYear = sDate.getFullYear().toString();
        }

        const loadedData = {
          ...initialValues,
          company: apiData.company?.name || apiData.company || "",
          companyAddress: apiData.companyAddress || "",
          year: apiData.year?.toString() || initialValues.year,
          openingExemptedAmount: apiData.openingExemptedAmount || "",
          openingAlreadyExemptedAmount: apiData.openingAlreadyExemptedAmount || "",
          items: mappedItems.length > 0 ? mappedItems : [],
          totalArisingExemptedAmount: apiData.totalArisingExemptedAmount || "",
          totalArisingAlreadyExemptedAmount: apiData.totalArisingAlreadyExemptedAmount || "",
          closingExemptedAmount: apiData.closingExemptedAmount || "",
          closingAlreadyExemptedAmount: apiData.closingAlreadyExemptedAmount || "",
          totalPages: apiData.totalPages || "",
          openingDate: apiData.openingDate
            ? new Date(apiData.openingDate).toLocaleDateString("vi-VN")
            : "",
          signingDay: sDay,
          signingMonth: sMonth,
          signingYear: sYear,
          preparerName: apiData.preparerName || "",
          chiefAccountantName: apiData.chiefAccountantName || "",
          legalRepresentativeName: apiData.legalRepresentativeName || "",
        };

        setFormInitials(loadedData);
        setGetData(apiData);
      } catch (error) {
        console.error("Lỗi getById", error);
        toast.error("Không thể tải dữ liệu");
      }
    })();
  }, [id]);

  const onChangeValue = useCallback((type, value) => {
    setData((prev) => ({ ...prev, [type]: value }));
  }, []);

  const handleBack = () => navigation(-1);

  // 3. Xử lý logic Submit (Tạo payload chuẩn theo Schema Backend)
  const submitForm = async (values) => {
    try {
      const num = (val) => (val && !isNaN(val) ? Number(val) : 0);

      // Ưu tiên lấy state 'data' từ child component (do child component control state)
      const getValue = (field) => (data[field] !== undefined ? data[field] : values[field]);

      const itemsFlat = getValue("items") || [];

      // Build array items chuẩn Backend
      const buildItems = (itemsList) =>
        itemsList.map((item) => ({
          documentNumber: item.documentNumber || "",
          documentDate: item.documentDate || null, // Có thể format lại chuẩn ISO Date nếu backend cần
          description: item.description || "",
          exemptedAmount: num(item.exemptedAmount),
          alreadyExemptedAmount: num(item.alreadyExemptedAmount),
        }));

      // Ráp ngày ký thành chuẩn ISO Date
      const signD = getValue("signingDay");
      const signM = getValue("signingMonth");
      const signY = getValue("signingYear");
      let signingDate = new Date().toISOString();
      if (signY && signM && signD) {
        signingDate = new Date(
          `${signY}-${String(signM).padStart(2, "0")}-${String(signD).padStart(2, "0")}`
        ).toISOString();
      }

      // Payload cuối cùng gửi xuống BE
      const payload = {
        company: getValue("company"),
        companyAddress: getValue("companyAddress"),
        year: num(getValue("year")),
        openingExemptedAmount: num(getValue("openingExemptedAmount")),
        openingAlreadyExemptedAmount: num(getValue("openingAlreadyExemptedAmount")),
        items: buildItems(itemsFlat),
        totalArisingExemptedAmount: num(getValue("totalArisingExemptedAmount")),
        totalArisingAlreadyExemptedAmount: num(getValue("totalArisingAlreadyExemptedAmount")),
        closingExemptedAmount: num(getValue("closingExemptedAmount")),
        closingAlreadyExemptedAmount: num(getValue("closingAlreadyExemptedAmount")),
        totalPages: num(getValue("totalPages")),
        openingDate: getValue("openingDate") || null,
        signingDate: signingDate,
        preparerName: getValue("preparerName"),
        chiefAccountantName: getValue("chiefAccountantName"),
        legalRepresentativeName: getValue("legalRepresentativeName"),
      };

      console.log("Submit payload:", JSON.stringify(payload, null, 2));

      if (!id) {
        await VatExemptionReductionService.create(payload);
        toast.success("Tạo sổ chi tiết thành công!");
      } else {
        await VatExemptionReductionService.update(id, payload);
        toast.success("Cập nhật sổ chi tiết thành công!");
      }

      // Chuyển hướng về trang list sau khi lưu thành công (Tuỳ chọn)
      // navigation("/your-list-route");
    } catch (error) {
      console.error("Lỗi khi gọi API lưu:", error);
      toast.error(error?.response?.data?.message || "Lưu thất bại!");
      throw error;
    }
  };

  const handlePrintDirect = (values) => {
    // Logic in sổ tương tự, bạn cần map lại các data truyền vào getPrintTemplate
    // sao cho khớp với template HTML của Mẫu S63-DN.
    const finalData = { ...values, ...data };

    const htmlContent = getPrintTemplate({
      headerInfo: finalData,
      rows: finalData.items || [],
      totals: {
        totalArisingExemptedAmount: finalData.totalArisingExemptedAmount,
        totalArisingAlreadyExemptedAmount: finalData.totalArisingAlreadyExemptedAmount,
        closingExemptedAmount: finalData.closingExemptedAmount,
        closingAlreadyExemptedAmount: finalData.closingAlreadyExemptedAmount,
      },
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const handleSubmit = async (values, actions) => {
    try {
      await submitForm(values);
    } catch (error) {
      // Error handled in submitForm
    } finally {
      if (actions) {
        actions.setTouched({});
        actions.setSubmitting(false);
      }
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={20}>
        <Grid container justifyContent="center" sx={{ height: "100%" }}>
          <Grid item xs={12} lg={12}>
            <Formik initialValues={formInitials} enableReinitialize={true} onSubmit={handleSubmit}>
              {({ values, errors, touched, isSubmitting, setFieldValue }) => (
                <Form autoComplete="off">
                  <Card sx={{ height: "100%" }}>
                    <SoftBox p={2}>
                      <SoftBox>
                        {/* COMPONENT GIAO DIỆN SỔ CHI TIẾT */}
                        <DetailLedgerVatExemptionReduction
                          formData={{
                            values,
                            touched,
                            errors,
                            getData,
                            onChangeValue,
                            setFieldValue,
                          }}
                        />

                        <SoftBox mt={10} width="100%" display="flex" justifyContent="space-between">
                          <SoftButton variant="gradient" color="light" onClick={handleBack}>
                            Trở về
                          </SoftButton>

                          {containView ? (
                            <SoftBox display="flex" gap={3}>
                              <SoftButton
                                variant="gradient"
                                color="success"
                                onClick={() => handlePrintDirect(values)}
                                type="button"
                              >
                                In Sổ Chi Tiết
                              </SoftButton>
                            </SoftBox>
                          ) : (
                            <SoftBox display="flex" gap={3}>
                              <SoftButton
                                disabled={isSubmitting}
                                type="submit"
                                variant="gradient"
                                color="dark"
                              >
                                {id ? "Lưu" : "Tạo"}
                              </SoftButton>
                            </SoftBox>
                          )}
                        </SoftBox>
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default DetailLedgerVatExemptionReductionDetail;
